import { FC, LegacyRef, useEffect, useState } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { GradientPage } from '@frontend/components';
import { Trans, useTranslation } from '@frontend/i18n';
import { TruelarkLeafCropped, WeaveTruelarkLogo } from '@frontend/promotion-assets#svg';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { pendo, sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, Heading, PopoverMenu, PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';
import {
  PromoSourceNotificationSourceMap,
  TRUE_LARK_MISSED_CALL_PROMO_FF,
  TrueLarkBannerDismissActions,
  TrueLarkDemoLinkBySourceMap,
} from '../../constants';
import { useIsTrueLarkEligible } from '../../hooks/use-is-true-lark-eligible';
import { useLast30DaysMissedCallCount } from '../../hooks/use-last-30-days-missed-call-count';
import { generateTrueLarkPromoTrackingId } from '../../tracking-ids';

interface TrueLarkMissedCallPromoBannerProps {
  containerStyles?: Interpolation<Theme>;
  innerRef?: LegacyRef<HTMLDivElement>;
  onDismiss?: () => void;
  source?: 'dashboard' | 'phone-analytics';
  locationIds?: string[];
}

const HOUR_IN_MS = 1000 * 60 * 60;

export const TrueLarkMissedCallPromoBanner: FC<TrueLarkMissedCallPromoBannerProps> = ({
  containerStyles,
  source = 'dashboard',
  onDismiss,
  locationIds,
  innerRef,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'true-lark' });
  const { getMenuProps, getTriggerProps, getItemProps } = usePopoverMenu({ placement: 'bottom-end' });
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useAppFlagStore();
  const [isDismissed, setIsDismissed] = useState(false);

  const isBannerEnabled = getFeatureFlagValue({
    locationIds: locationIds || selectedLocationIds || [],
    flagName: TRUE_LARK_MISSED_CALL_PROMO_FF,
  });

  const { data: dismissStatusData, isLoading: isDismissStatusLoading } =
    PlatformLocationFeatureV1.Queries.useCheckFeatureNotificationStatusQuery({
      request: {
        notificationFeature:
          PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_TRUELARK_NWX_PROMO,
        sourceEnum: PromoSourceNotificationSourceMap[source],
      },
      options: {
        retry: false,
        enabled: isBannerEnabled,
        cacheTime: HOUR_IN_MS,
        staleTime: HOUR_IN_MS,
      },
    });

  const isBannerDismissed =
    isDismissed ||
    isDismissStatusLoading ||
    !dismissStatusData?.currentStatus ||
    [
      PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN,
      PlatformLocationFeatureV1.Types.Action.ACTION_REMIND_AFTER_3_DAYS,
    ].includes(dismissStatusData?.currentStatus);

  const { isEligible: isTruelarkEligible, syncAppEligibleLocationIds } = useIsTrueLarkEligible({
    locationIds,
    enabled: isBannerEnabled && !isBannerDismissed,
  });

  const { missedCallCount } = useLast30DaysMissedCallCount({
    locationIds: syncAppEligibleLocationIds,
    enabled: isBannerEnabled && isTruelarkEligible,
  });

  const { mutate: dismissBannerMutate, isLoading: isDismissInProgress } =
    PlatformLocationFeatureV1.Mutations.useCreateOrUpdateFeatureNotificationUserActionMutation({
      options: {
        onSuccess: onDismiss,
        onError: (error) => {
          sentry.warn({
            error: `Error dismissing Truelark missed call banner: ${error}`,
            topic: 'customer-lifecycle-management',
            team: 'fe-clm',
            addContext: {
              name: 'error',
              context: { error, source },
            },
          });
        },
      },
    });

  const handleDemoClick = () => {
    const demoLink = TrueLarkDemoLinkBySourceMap[source];
    window.open(demoLink, '_blank', 'noopener,noreferrer');
  };

  const handleDismissAction = (action: PlatformLocationFeatureV1.Types.Action) => {
    dismissBannerMutate({
      notificationFeature: PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_TRUELARK_NWX_PROMO,
      // Dismiss on all sources
      sourceEnum: [
        PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_TEASER_BANNER,
        PlatformLocationFeatureV1.Types.Source.SOURCE_PHONE_ANALYTICS_TEASER,
      ],
      actionEnum: action,
    });
    setIsDismissed(true);
  };

  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      pendo.track(
        generateTrueLarkPromoTrackingId({
          source,
          action: 'banner-visible',
        })
      );
    }
  }, [inView, source]);

  if (!isBannerEnabled || !isTruelarkEligible || isBannerDismissed || !missedCallCount || missedCallCount === 0) {
    return null;
  }

  return (
    <div
      css={[defaultStyles.container, containerStyles]}
      data-testid='true-lark-missed-call-promo-banner'
      ref={innerRef}
    >
      <GradientPage gradientColor1={theme.colors.secondary.seaweed5} gradientColor2={theme.colors.indigo10}>
        <TruelarkLeafCropped css={defaultStyles.bannerBGImage} customFallback={<></>} />
        <div css={defaultStyles.content} ref={inViewRef}>
          <WeaveTruelarkLogo css={defaultStyles.logoImage} customFallback={<></>} />
          <Heading level={3}>
            <Trans t={t} count={missedCallCount} values={{ missedCallCount }}>
              You missed <span css={defaultStyles.callsCountText}>{{ missedCallCount }} calls</span> last month.
            </Trans>
          </Heading>
          <Text size='medium'>{t('See how TrueLark can answer all your calls.')}</Text>
          <div css={defaultStyles.actionBar}>
            <Button
              variant='primary'
              onClick={handleDemoClick}
              trackingId={generateTrueLarkPromoTrackingId({
                source,
                action: 'demo-action-click',
              })}
            >
              {t('Get a Demo')}
            </Button>

            <Button
              variant='tertiary'
              {...getTriggerProps()}
              css={{ color: theme.colors.neutral50 }}
              trackingId={generateTrueLarkPromoTrackingId({
                source,
                action: 'maybe-later-action-click',
              })}
              disabled={isDismissInProgress}
            >
              {t('Maybe Later')}
            </Button>
          </div>
        </div>
      </GradientPage>

      <PopoverMenu {...getMenuProps()}>
        {TrueLarkBannerDismissActions.map(({ action, trackingId, label }, index) => (
          <PopoverMenuItem
            key={action}
            trackingId={generateTrueLarkPromoTrackingId({
              source,
              action: trackingId,
            })}
            {...getItemProps({ index, onClick: () => handleDismissAction(action) })}
          >
            {label}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </div>
  );
};

const defaultStyles = {
  container: css`
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadius.large};
    box-shadow: ${theme.shadows.floating};
    position: relative;
    container-type: inline-size;
    z-index: 0;
    overflow: hidden;
  `,
  callsCountText: css`
    background: linear-gradient(280.99deg, #02cad2 2.97%, #0891c4 91.86%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(1)};
    position: relative;
  `,
  actionBar: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-top: ${theme.spacing(1)};
  `,
  bannerBGImage: css`
    position: absolute;
    top: 0;
    height: 100%;
    width: 232px;
    right: 100px;
    @container (max-width: ${breakpoints.small.min}px) {
      display: none;
    }
  `,
  logoImage: css`
    position: absolute;
    top: ${theme.spacing(2)};
    right: ${theme.spacing(2)};
    width: 377px;
    height: auto;
    mix-blend-mode: multiply;
    @container (max-width: ${breakpoints.medium.min}px) {
      display: none;
    }
  `,
};
